import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { clearAllUXHints } from "./UXHint";

/**
 * ClientHelpModal — notice utilisateur côté client
 * Même structure que HelpModal (vendeur) mais contenu adapté client.
 *
 * Props :
 *   onClose() — fermer la notice
 */

const SECTIONS = [
  {
    emoji: "📷",
    title: "Crush un article",
    body: "Appuie sur « Crush un article » puis scanne le QR code ou le code-barres d'un produit. Il est immédiatement mémorisé dans ta liste.",
  },
  {
    emoji: "💜",
    title: "Retrouver tes crushs",
    body: "Tous tes articles scannés sont dans l'onglet Crushs. Filtre par statut (en stock, promotion…) ou par ville. Tu peux aussi partager un crush en un clic — le lien fonctionne même sans compte CrushNow.",
  },
  {
    emoji: "🔔",
    title: "Activer une alerte",
    body: "Sur chaque produit de tes Crushs, clique sur la cloche pour suivre ses mises à jour. Quand un vendeur modifie le prix, le stock ou la disponibilité, tu reçois une notification — sans rien faire.",
  },
  {
    emoji: "🗺️",
    title: "Magasins favoris",
    body: "Depuis l'onglet Carte, ajoute tes magasins en favoris. Tu recevras uniquement les notifications des magasins que tu as choisis — pas de spam inutile.",
  },
  {
    emoji: "🛍️",
    title: "Mes achats",
    body: "Quand tu achètes un produit crushed, appuie sur ✓ J'ai acheté ce produit. Il rejoint ton historique dans l'onglet Achats et tu gagnes +3 XP.",
  },
  {
    emoji: "📦",
    title: "Produit inconnu ?",
    body: "Si un code-barres n'est pas reconnu, crée une fiche manuellement : nom, catégorie, photo optionnelle. Tu contribues à la base communautaire et tu gagnes +5 XP.",
  },
  {
    emoji: "👤",
    title: "Ton profil et ta progression",
    body: "Sur ta page profil, tu retrouves ton niveau, tes badges et ton historique XP. Plus tu utilises CrushNow, plus tu progresses parmi les 7 niveaux : Curieux → Explorateur → Chasseur → Crusheur → Expert → Ambassadeur → Légende.\n\nTu gagnes des XP en :\n- Scannant un produit (+1 XP)\n- Ajoutant un produit communautaire (+5 XP)\n- Partageant un crush (+2 XP)\n- Enregistrant un achat (+3 XP)\n- Complétant ton profil (+10 XP, une fois)\n\n12 badges sont à débloquer — certains se déverrouillent automatiquement selon ton activité. Ta progression est sauvegardée en ligne, sur tous tes appareils.",
    highlight: true,
  },
  {
    emoji: "💡",
    title: "Le saviez-vous ?",
    body: "CrushNow ne diffuse aucune publicité. Le service est gratuit pour les clients. Tes données ne sont jamais revendues.\nÀ venir : mini-défis hebdo et collections de produits !",
  },
];

const ClientHelpModal = ({ onClose }) => {
  const [resetDone, setResetDone] = useState(false);

  const handleResetHints = () => {
    clearAllUXHints();
    setResetDone(true);
  };

  // ESC ferme
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Scroll lock
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const content = (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          top: 0, left: 0,
          width: "100%", height: "100%",
          zIndex: 8999,
          background: "rgba(15, 23, 42, 0.48)",
        }}
      />

      {/* Panel centré */}
      <div
        style={{
          position: "fixed",
          top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 9000,
          background: "white",
          borderRadius: "16px",
          width: "calc(100% - 32px)",
          maxWidth: "480px",
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
          overflowY: "auto",
        }}
      >
        {/* En-tête */}
        <div
          style={{
            position: "sticky",
            top: 0,
            background: "white",
            zIndex: 1,
            padding: "20px 20px 14px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 900, color: "#0f172a" }}>
              💜 Comment ça marche ?
            </h3>
            <p style={{ margin: "3px 0 0", fontSize: "0.75rem", color: "#94a3b8" }}>
              Lecture rapide · moins de 30 secondes
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "#F1F5F9",
              border: "none",
              borderRadius: "8px",
              padding: "6px 11px",
              cursor: "pointer",
              fontSize: "1rem",
              lineHeight: 1,
              color: "#475569",
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        {/* Corps */}
        <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "12px" }}>
          {SECTIONS.map((s) => (
            <div
              key={s.title}
              style={{
                padding: "14px 16px",
                borderRadius: "10px",
                background: s.highlight ? "#FFF0F6" : "#F8FAFC",
                border: s.highlight ? "1px solid #FBCFE8" : "1px solid transparent",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                <span style={{ fontSize: "1.1rem" }}>{s.emoji}</span>
                <strong style={{ fontSize: "0.875rem", color: "#0f172a", fontWeight: 800 }}>
                  {s.title}
                </strong>
              </div>
              <p style={{ margin: 0, fontSize: "0.82rem", color: "#475569", lineHeight: 1.55 }}>
                {s.body}
              </p>
            </div>
          ))}
        </div>

        {/* Pied */}
        <div
          style={{
            position: "sticky",
            bottom: 0,
            background: "white",
            borderTop: "1px solid #e2e8f0",
            padding: "14px 20px",
            flexShrink: 0,
          }}
        >
          <button
            onClick={onClose}
            style={{
              width: "100%",
              padding: "11px",
              borderRadius: "8px",
              border: "none",
              background: "linear-gradient(135deg, #FF8C00, #FF0080)",
              color: "white",
              fontWeight: 700,
              fontSize: "0.875rem",
              cursor: "pointer",
              touchAction: "manipulation",
            }}
          >
            Compris !
          </button>
          <button
            onClick={handleResetHints}
            style={{
              width: "100%",
              marginTop: "8px",
              padding: "9px",
              borderRadius: "8px",
              border: "none",
              background: "transparent",
              color: resetDone ? "#16a34a" : "#94a3b8",
              fontWeight: 600,
              fontSize: "0.75rem",
              cursor: "pointer",
              textTransform: "none",
            }}
          >
            {resetDone ? "✅ Conseils réactivés — visibles au prochain chargement" : "↺ Réafficher tous les conseils"}
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
};

export default ClientHelpModal;
